import { useGetPrevTabIndexCookie } from '@hooks/useGetPrevTabIndexCookie';
import useResizeView from '@hooks/useResizeView';
import { cookieStore } from '@store/cookie/cookieStore';
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  CSSProperties,
} from 'react';
import styled from 'styled-components';

import { getTabStyle } from '../calcTab';
import {
  CountSpan,
  TabBtnContainer,
  TabBtnWrapper,
  TabNoAniUnderline,
  TabUnderLine,
} from '../tabStyle';
import TabButton from '@components/dataEntry/button/TabButton';
import IconButton from '@components/dataEntry/button/IconButton';
import { Link, useNavigate } from 'react-router-dom';
import {
  PAGE_HASHTAG_TYPE,
  PAGE_HASHTAG_TYPE_VALUE,
} from '@typings/setting/pageTagType.interface';

const ImgTabImgWrapper = styled.div`
  display: inline-grid;

  .img-normal,
  .img-hover {
    grid-area: 1 / 1;
    display: block;
    object-fit: contain;
    transition: opacity 0.2s ease;
  }

  .img-hover {
    opacity: 0;
    pointer-events: none;
  }
`;

interface Props {
  layout?: PAGE_HASHTAG_TYPE_VALUE;
  tabList: any[];
  position?: 'flex-start' | 'center' | 'space-between';
  width?: number;
  setCurrent: any;
  currentOrder: number;
  setSelectedValue?: any | null;
  alloption?: boolean;
  allcount?: number;
  tabFixed?: boolean;
  btnSideSec?: JSX.Element | JSX.Element[];
  style?: CSSProperties;
  tabId?: string;
  customBtnId?: string;
  noShowCount?: boolean;
  noUnderlineAni?: boolean;
  handleOffset?: boolean;
  tabMaxHeight?: number;
}

function TabBtns({
  layout = PAGE_HASHTAG_TYPE.HORIZONTAL,
  tabList,
  currentOrder,
  setCurrent,
  position = 'center',
  setSelectedValue,
  alloption,
  allcount,
  tabFixed,
  btnSideSec,
  style,
  tabId,
  customBtnId = '',
  noShowCount,
  noUnderlineAni = false,
  handleOffset = true,
  tabMaxHeight = 80,
}: Props) {
  const navigate = useNavigate();
  const tabWrapRef = useRef<HTMLDivElement>(null);
  const [selectedTabStyle, setSelectedTabStyle] = useState<any>(
    getTabStyle(currentOrder, customBtnId),
  );
  const currentTab = alloption
    ? tabList[currentOrder - 1]
    : tabList[currentOrder];
  const hideUnderline = !!currentTab?.imgURL;

  const [mouseDown, setMouseDown] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, x: 0, y: 0 });
  const [maskingDir, setMaskingDir] = useState<
    '' | 'right' | 'left' | 'center'
  >('');

  const [prevTabId, prevTabIndex] = useGetPrevTabIndexCookie({
    tabId,
    tabList,
  });

  // 좌우 스크롤 화살표 관련 상태
  const ARROW_BTN_WIDTH = 36; // IconButton lg 사이즈 기준
  const ARROW_SIDE_PADDING = ARROW_BTN_WIDTH + 8; // 버튼 여백 포함 시각적 패딩
  const [showArrows, setShowArrows] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const didInitScrollLeft = useRef(false);

  const updateScrollState = useCallback(() => {
    const node = tabWrapRef.current;
    if (!node) return;
    const { scrollWidth, clientWidth, scrollLeft } = node;
    const needArrows = scrollWidth > clientWidth + 1; // 실제 오버플로우 여부만 체크
    setShowArrows(needArrows);

    if (!needArrows) {
      setMaskingDir('');
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const atStart = scrollLeft <= 0;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
    setCanScrollLeft(!atStart);
    setCanScrollRight(!atEnd);
    setMaskingDir(atStart ? 'right' : atEnd ? 'left' : 'center');
  }, []);

  // 위치 계산 함수 개선
  const updateTabPosition = useCallback(() => {
    const newStyle = getTabStyle(currentOrder, customBtnId);

    // 유효한 값인지 확인
    if (newStyle.width > 0) {
      setSelectedTabStyle(newStyle);
    }
  }, [currentOrder, customBtnId]);

  // DOM 변경 감지를 위한 MutationObserver 설정
  useEffect(() => {
    const tabWrapNode = document.querySelector(`#tab_btn_wrap${customBtnId}`);
    if (!tabWrapNode) return;

    const observer = new MutationObserver(() => {
      // DOM 구조 변경 시 위치 재계산
      setTimeout(() => {
        updateTabPosition();
        updateScrollState();
      }, 0);
    });

    observer.observe(tabWrapNode, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => observer.disconnect();
  }, [updateTabPosition, updateScrollState, customBtnId]);

  // 스크롤 이벤트 리스너 추가 + 화살표/마스킹 갱신
  useEffect(() => {
    const node = tabWrapRef.current;
    if (!node) return;

    const handleScroll = () => {
      setTimeout(() => {
        updateTabPosition();
        updateScrollState();
      }, 0);
    };

    node.addEventListener('scroll', handleScroll);
    // 초기 상태 동기화
    setTimeout(() => {
      updateScrollState();
      // 최초 렌더 시 스크롤을 항상 좌측 0으로 고정(쿠키가 없거나 첫 탭일 때만)
      if (
        !didInitScrollLeft.current &&
        (!tabId || prevTabIndex === 0 || prevTabIndex === undefined)
      ) {
        node.scrollLeft = 0;
        didInitScrollLeft.current = true;
        updateScrollState();
      }
    }, 0);

    return () => node.removeEventListener('scroll', handleScroll);
  }, [updateTabPosition, updateScrollState, customBtnId, tabId, prevTabIndex]);

  const handleClickTab = useCallback(
    (index: number, id) => {
      // 커스텀 탭 클릭 액션 실행 (있는 경우)
      const actualIndex = alloption ? index - 1 : index;
      if (tabList[actualIndex]?.onTabClick) {
        tabList[actualIndex].onTabClick();
      }

      if (setSelectedValue) setSelectedValue(id);

      setCurrent(index);

      // 위치 계산을 약간 지연시켜 DOM 업데이트 완료 후 실행
      setTimeout(() => {
        const newStyle = getTabStyle(index, customBtnId);
        if (newStyle.width > 0) {
          setSelectedTabStyle(newStyle);
        }
        updateScrollState();
      }, 0);

      // 해시태그 클릭 때 마다 1페이지로 돌아가게 함
      if (handleOffset) navigate(`?offset=0`);

      // tab id cookie 저장
      if (tabId) {
        cookieStore.setTabHistory(tabId, id);
      }
    },
    [customBtnId, handleOffset, tabId, updateScrollState, tabList, alloption],
  );

  /* drag 가능 */
  const handleMouseDown = useCallback(
    (e) => {
      const tabWrapNode = document.querySelector(`#tab_btn_wrap${customBtnId}`);
      setMouseDown(true);
      setPos({
        left: tabWrapNode!.scrollLeft,
        top: tabWrapNode!.scrollLeft,
        // Get the current mouse position
        x: e.clientX,
        y: e.clientY,
      });
    },
    [customBtnId],
  );

  const handleMouseMove = useCallback(
    (e) => {
      const tabWrapNode = document.querySelector(`#tab_btn_wrap${customBtnId}`);

      if (mouseDown) {
        const dx = e.clientX - pos.x;
        const dy = e.clientY - pos.y;

        // Scroll the element
        tabWrapNode!.scrollTop = pos.top - dy;
        tabWrapNode!.scrollLeft = pos.left - dx;
      }
    },
    [mouseDown, customBtnId],
  );

  const handleMouseUp = useCallback((e) => {
    setMouseDown(false);
  }, []);
  /* drag 가능 end */

  useEffect(() => {
    // local cookie에 tab id 저장
    if (tabId) {
      setSelectedValue(prevTabId);
      setCurrent(prevTabIndex);

      // 쿠키에서 불러온 탭 위치 계산
      setTimeout(() => {
        const newStyle = getTabStyle(prevTabIndex, customBtnId);
        if (newStyle.width > 0) {
          setSelectedTabStyle(newStyle);
        }
        updateScrollState();
      }, 0);
    }
  }, [prevTabId, tabList, prevTabIndex, customBtnId, updateScrollState]);

  useEffect(() => {
    // 현재 탭의 위치 재계산
    setTimeout(() => {
      updateTabPosition();
      updateScrollState();
    }, 0);
  }, [
    currentOrder,
    customBtnId,
    tabList,
    updateTabPosition,
    updateScrollState,
  ]);

  // 브라우저 사이즈 바뀔때마다 underline값 다시 계산 + 화살표 상태 갱신
  useResizeView(() => {
    // 리사이즈 시 약간의 지연을 두어 레이아웃 완료 후 계산
    setTimeout(() => {
      updateTabPosition();
      updateScrollState();
    }, 100);
  }, currentOrder);

  const handleScrollLeft = useCallback(() => {
    const node = tabWrapRef.current;
    if (!node) return;
    const step = Math.max(80, Math.floor(node.clientWidth * 0.8));
    node.scrollBy({ left: -step, behavior: 'smooth' });
  }, []);

  const handleScrollRight = useCallback(() => {
    const node = tabWrapRef.current;
    if (!node) return;
    const step = Math.max(80, Math.floor(node.clientWidth * 0.8));
    node.scrollBy({ left: step, behavior: 'smooth' });
  }, []);

  return (
    <TabBtnContainer
      style={{ ...style }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      role="tab"
    >
      {/* 좌측 스크롤 화살표 - flex 공간 차지 */}
      {showArrows && (
        <div style={{ flexShrink: 0, alignSelf: 'center' }}>
          <IconButton
            size="lg"
            shape="stroke"
            icon="LeftArrowO"
            onClick={handleScrollLeft}
            disabled={!canScrollLeft}
          />
        </div>
      )}

      <TabBtnWrapper
        style={
          showArrows
            ? { justifyContent: 'flex-start' }
            : position
            ? { justifyContent: position }
            : {}
        }
        ref={tabWrapRef}
        id={`tab_btn_wrap${customBtnId}`}
        isFixed={tabFixed}
        maskingdir={maskingDir}
        layout={layout}
        // scroll drag
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        // scroll drag end
      >
        {alloption && tabList && (
          <TabButton
            id={`tab_label_0${customBtnId}`}
            onClick={(e) => handleClickTab(0, 'all')}
            className={`${currentOrder === 0 && 'tabActive'}`}
            layout={layout}
          >
            <span>
              전체보기
              {!noShowCount && <CountSpan>&nbsp;{allcount || 0}</CountSpan>}
            </span>
          </TabButton>
        )}

        {tabList &&
          tabList.map((list, index) => {
            const { value, docIdList, id, noShow, isShow, linkto, query } =
              list;
            const isActive = currentOrder === (!alloption ? index : index + 1);
            const isImageTab = !!list.imgURL;
            if (noShow) return;
            if (!(isShow ?? true)) return;

            if (linkto) {
              return (
                <Link
                  to={`${linkto}${query ? `?query=${query}` : ''}`}
                  key={index}
                >
                  <TabButton
                    key={index}
                    id={`tab_label_${index}${customBtnId}`}
                    onClick={(e) => handleClickTab(index, id)}
                    className={`${currentOrder === index && 'tabActive'}`}
                    layout={layout}
                  >
                    {value}
                  </TabButton>
                </Link>
              );
            }

            return (
              <TabButton
                key={index}
                id={
                  !alloption
                    ? `tab_label_${index}${customBtnId}`
                    : `tab_label_${index + 1}${customBtnId}`
                }
                onClick={(e) =>
                  handleClickTab(!alloption ? index : index + 1, id)
                }
                className={`${isActive && 'tabActive'}`}
                layout={layout}
                isImage={isImageTab}
              >
                {list.imgURL ? (
                  <ImgTabImgWrapper>
                    <img
                      className="img-normal"
                      src={list.imgURL}
                      alt={String(value || '')}
                      title={String(value || '')}
                      style={{ maxHeight: tabMaxHeight || 80 }}
                    />
                    {list.hoverImgURL && (
                      <img
                        className="img-hover"
                        src={list.hoverImgURL}
                        alt={String(value || '')}
                        style={{ maxHeight: tabMaxHeight || 80 }}
                      />
                    )}
                  </ImgTabImgWrapper>
                ) : (
                  value
                )}
                {!noShowCount && docIdList && (
                  <CountSpan>&nbsp;{docIdList.length}</CountSpan>
                )}
                {noUnderlineAni && isActive && !isImageTab && (
                  <TabNoAniUnderline />
                )}
              </TabButton>
            );
          })}

        {!noUnderlineAni &&
          layout !== PAGE_HASHTAG_TYPE.SPREAD &&
          !hideUnderline && <TabUnderLine {...selectedTabStyle} />}
      </TabBtnWrapper>

      {/* 우측 스크롤 화살표 - flex 공간 차지 */}
      {showArrows && (
        <div style={{ flexShrink: 0, alignSelf: 'center' }}>
          <IconButton
            size="lg"
            shape="stroke"
            icon="RightArrowO"
            onClick={handleScrollRight}
            disabled={!canScrollRight}
            tooltipDir="left"
          />
        </div>
      )}

      {/* btnSideSec - 우측 화살표 다음에 고정 자리 차지 */}
      {btnSideSec && (
        <div style={{ flexShrink: 0, alignSelf: 'center' }}>{btnSideSec}</div>
      )}
    </TabBtnContainer>
  );
}

export default TabBtns;
